import { stdevAndMean } from "@ugg/shared/utils/math";
import { DuoTierList, Duo, DuoRole, DuoData } from "@ugg/shared/interfaces/tier-lists/duo-tier-list.interface";

export interface DuoInfo {
  duo_1: Duo[0];
  duo_1_role: Duo[1];
  duo_1_wins: Duo[2];
  duo_1_matches: Duo[3];
  duo_1_winrate: number;
  duo_1_link: {
    championId: Duo[0];
    role: Duo[1];
  };
  duo_2: Duo[4];
  duo_2_role: Duo[5];
  duo_2_wins: Duo[6];
  duo_2_matches: Duo[7];
  duo_2_winrate: number;
  duo_2_link: {
    championId: Duo[4];
    role: Duo[5];
  };
  duo_combined_wins: Duo[8];
  duo_combined_matches: Duo[9];
  duo_bans: Duo[10];
  duo_combined_winrate: number;
  duo_combined_banrate: number;
  duo_combined_pickrate: number;
  plus_minus_winrate: number;
  effective_winrate: number;
  stdevs: number | undefined;
}

export function parseDuoTierList(data: DuoTierList) {
  const [duoData, lastUpdated, totalMatches] = data;

  const duoPairs = Object.keys(duoData) as DuoRole[];

  let newDuoData: Record<DuoRole, DuoInfo[]> = {
    adc_supp: [],
    mid_jungle: [],
    top_jungle: [],
    jungle_supp: [],
  };

  duoPairs.forEach((duo) => {
    newDuoData[duo] = duoData[duo].map((championData) => {
      const [
        duo_1,
        duo_1_role,
        duo_1_wins,
        duo_1_matches,
        duo_2,
        duo_2_role,
        duo_2_wins,
        duo_2_matches,
        duo_combined_wins,
        duo_combined_matches,
        duo_bans,
      ] = championData;

      const duo_1_link = {
        championId: duo_1,
        role: duo_1_role,
      };
      const duo_2_link = {
        championId: duo_2,
        role: duo_2_role,
      };
      const duo_1_winrate = (duo_1_wins / duo_1_matches) * 100;
      const duo_2_winrate = (duo_2_wins / duo_2_matches) * 100;
      const duo_combined_winrate = (duo_combined_wins / duo_combined_matches) * 100;
      const duo_combined_banrate = (duo_bans / totalMatches) * 100;
      const duo_combined_pickrate = (duo_combined_matches / totalMatches) * 100;
      const plus_minus_winrate = duo_combined_winrate - Math.sqrt(duo_1_winrate * duo_2_winrate);

      const effective_winrate =
        ((duo_combined_banrate / 100) * (((duo_combined_winrate * (1 + plus_minus_winrate)) / 100 + 0.55) / 2) +
          (duo_combined_pickrate / 100) * ((duo_combined_winrate * (1 + plus_minus_winrate)) / 100) +
          (1 - duo_combined_banrate / 100 - duo_combined_pickrate / 100) * 0.5) *
        100;

      const cleanData: DuoInfo = {
        duo_1,
        duo_1_role,
        duo_1_wins,
        duo_1_matches,
        duo_1_winrate,
        duo_1_link,
        duo_2,
        duo_2_role,
        duo_2_wins,
        duo_2_matches,
        duo_2_winrate,
        duo_2_link,
        duo_combined_wins,
        duo_combined_matches,
        duo_bans,
        duo_combined_winrate,
        duo_combined_banrate,
        duo_combined_pickrate,
        plus_minus_winrate,
        effective_winrate,
        stdevs: undefined,
      };

      return cleanData;
    });
  });

  duoPairs.forEach((duo) => {
    const effective_winrates = newDuoData[duo].map((x) => x.effective_winrate) as number[];
    const maths = stdevAndMean(effective_winrates);

    newDuoData[duo].forEach((championData) => {
      if (championData.effective_winrate) {
        championData["stdevs"] = (championData.effective_winrate - maths.mean) / maths.stdev;
      }
    });

    newDuoData[duo].sort((a, b) => {
      if (b.duo_combined_winrate !== undefined && a.duo_combined_winrate !== undefined) {
        return b.duo_combined_winrate - a.duo_combined_winrate;
      }
      return 0;
    });
  });

  return {
    data: newDuoData,
    lastUpdated,
    totalMatches: totalMatches,
  };
}
